import React from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Avatar,
  Chip,
  Skeleton,
  useMediaQuery,
  useTheme,
  LinearProgress,
} from '@mui/material';
import {
  EmojiEvents as BadgeIcon,
  Lock as LockedIcon,
} from '@mui/icons-material';
import { userApi, Badge } from '../services/api';

function BadgesPage() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const { data: badges, isLoading } = useQuery({
    queryKey: ['all-badges'],
    queryFn: () => userApi.getAllBadges().then((r) => r.data),
  });

  if (isLoading) {
    return (
      <Box>
        <Typography variant="h4" gutterBottom>Badges</Typography>
        <Grid container spacing={3}>
          {[1, 2, 3, 4, 5, 6].map((i) => (
            <Grid item xs={6} sm={4} md={3} key={i}>
              <Skeleton variant="rectangular" height={200} sx={{ borderRadius: 2 }} />
            </Grid>
          ))}
        </Grid>
      </Box>
    );
  }

  const earnedBadges = badges?.filter((b) => b.earned) || [];
  const lockedBadges = badges?.filter((b) => !b.earned) || [];

  // Calculate progress
  const progressPercent = badges?.length ? Math.round((earnedBadges.length / badges.length) * 100) : 0;

  return (
    <Box>
      <Typography variant={isMobile ? 'h5' : 'h4'} gutterBottom>Badges</Typography>

      {/* Progress indicator */}
      <Box sx={{ mb: 3 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
          <Typography variant="body2" color="text.secondary">
            {earnedBadges.length} von {badges?.length || 0} Badges erhalten
          </Typography>
          <Typography variant="body2" fontWeight="medium">
            {progressPercent}%
          </Typography>
        </Box>
        <LinearProgress
          variant="determinate"
          value={progressPercent}
          sx={{ height: 8, borderRadius: 4 }}
        />
      </Box>

      {/* Earned Badges */}
      {earnedBadges.length > 0 && (
        <>
          <Typography variant={isMobile ? 'subtitle1' : 'h6'} fontWeight="medium" gutterBottom sx={{ mt: 2 }}>
            Verdiente Badges ({earnedBadges.length})
          </Typography>
          <Grid container spacing={{ xs: 1.5, sm: 3 }} sx={{ mb: 4 }}>
            {earnedBadges.map((badge) => (
              <Grid item xs={6} sm={4} md={3} key={badge.id}>
                <Card sx={{ textAlign: 'center', height: '100%' }}>
                  <CardContent sx={{ p: isMobile ? 1.5 : 2 }}>
                    <Avatar
                      sx={{
                        width: isMobile ? 56 : 80,
                        height: isMobile ? 56 : 80,
                        mx: 'auto',
                        mb: isMobile ? 1 : 2,
                        bgcolor: 'primary.main',
                      }}
                      src={badge.imageUrl || undefined}
                    >
                      <BadgeIcon sx={{ fontSize: isMobile ? 28 : 40 }} />
                    </Avatar>
                    <Typography variant={isMobile ? 'body1' : 'h6'} fontWeight="medium" gutterBottom noWrap>
                      {badge.name}
                    </Typography>
                    {!isMobile && (
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        {badge.description}
                      </Typography>
                    )}
                    <Chip
                      size="small"
                      label={`+${badge.pointsValue} P`}
                      color="primary"
                    />
                    {badge.earnedAt && !isMobile && (
                      <Typography variant="caption" display="block" sx={{ mt: 1 }}>
                        {new Date(badge.earnedAt).toLocaleDateString('de-DE')}
                      </Typography>
                    )}
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </>
      )}

      {/* Locked Badges */}
      {lockedBadges.length > 0 && (
        <>
          <Typography variant={isMobile ? 'subtitle1' : 'h6'} fontWeight="medium" gutterBottom sx={{ mt: 2 }}>
            Noch zu verdienen ({lockedBadges.length})
          </Typography>
          <Grid container spacing={{ xs: 1.5, sm: 3 }}>
            {lockedBadges.map((badge) => (
              <Grid item xs={6} sm={4} md={3} key={badge.id}>
                <Card sx={{ textAlign: 'center', height: '100%', opacity: 0.7 }}>
                  <CardContent sx={{ p: isMobile ? 1.5 : 2 }}>
                    <Avatar
                      sx={{
                        width: isMobile ? 56 : 80,
                        height: isMobile ? 56 : 80,
                        mx: 'auto',
                        mb: isMobile ? 1 : 2,
                        bgcolor: 'grey.400',
                      }}
                    >
                      <LockedIcon sx={{ fontSize: isMobile ? 28 : 40 }} />
                    </Avatar>
                    <Typography variant={isMobile ? 'body1' : 'h6'} fontWeight="medium" gutterBottom noWrap>
                      {badge.name}
                    </Typography>
                    {!isMobile && (
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        {badge.description}
                      </Typography>
                    )}
                    <Chip
                      size="small"
                      label={`+${badge.pointsValue} P`}
                      variant="outlined"
                    />
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </>
      )}
    </Box>
  );
}

export default BadgesPage;
